/** @module imports/maper */

/**
 * map data
 * @param  {Object} course response data object that received from server
 * @return {Course}
 */
export function map2CourseObj(course) {
    let teachers = [];
    if (course["lecturers"]) {
        teachers = course["lecturers"].map(teacher => ({
            id: teacher["uid"],
            fullName: teacher["name"],
            image: teacher["image"],
            education: teacher["education"],
            summary: teacher["summary"],
            role: teacher["role"]
        }));
    }

    const courseObj = {
        id: course["uid"],
        title: course["title"],
        description: course["description"],
        secondDescription: course["second_description"],
        summary: course["summary"],
        image: course["image"],
        video: course["video"],
        isFeature: course["is_featured"],
        isActive: course["is_active"],
        startDate: course["start_date"],
        endDate: course["end_date"],
        duration: course["course_duration_in_days"],
        term: course["term"],
        teachers: teachers,
        chapters: [],
        fullAccess: course["full_access"],
        userAccess: course["user_access"]
    };
    return courseObj;
}

export function map2Attachment(attachment) {
    let status = null;
    if (attachment["is_verified"]) {
        status = "verified";
    } else {
        if (attachment["file"]) {
            status = "uploaded";
        } else {
            status = "empty";
        }
    }
    return {
        id: attachment["uid"],
        title: attachment["title"],
        file: attachment["file"],
        status
    };
}

/**
 * map data
 * @param  {Object} program response data object that received from server
 * @return {Program}
 */
export function map2programObj(program) {
    let courses = [];
    if (program["courses"]) {
        courses = program["courses"].map(course => {
            return map2CourseObj(course);
        });
    }

    let attachments = [];
    if (program["required_attachment_titles"]) {
        attachments = program["required_attachment_titles"].map(at => {
            return {
                id: at["uid"],
                title: at["title"]
            };
        });
    }

    const programObj = {
        id: program["uid"],
        title: program["title"],
        description: program["description"],
        summary: program["summary"],
        status: program["status"],
        image: program["image"],
        video: program["video"],
        programType: program["program_type"],
        price: program["amount"],
        capacity: program["capacity"],
        startDateTime: program["start_date"],
        courseCount: program["courses_count"],
        enrollDate: program["enroll_date"],
        attachments,
        courses
    };
    return programObj;
}

/**
 * map data
 * @param  {Object} teacher response data object that received from server
 * @return {Teacher}
 */
export function map2TeacherObj(teacher) {
    const teacherObj = {
        id: teacher["uid"],
        fullName: teacher["name"],
        image: teacher["image"],
        summary: teacher["summary"],
        education: teacher["education"],
        description: teacher["description"],
        isFeature: teacher["is_featured"],
        courseCount: teacher["courses_count"]
    };
    return teacherObj;
}

/**
 * map data
 * @param  {Object} chapter response data object that received from server
 * @return {Chapter}
 */
export function map2ChapterObj(chapter) {
    let sections = [];
    if (chapter["sections"]) {
        sections = chapter["sections"].map(section => {
            let units = [];
            if (section["units"]) {
                units = section["units"].map(unit => {
                    return {
                        id: unit["uid"],
                        title: unit["title"],
                        type: unit["content_type"],
                        description: unit["description"],
                        isSeen: unit["is_seen"]
                    };
                });
            }

            return {
                id: section["uid"],
                title: section["title"],
                description: section["description"],
                isCurrent: false,
                units
            };
        });
    }

    let period = null;
    if (chapter["period"]) {
        period = {
            id: chapter["period"]["uid"],
            title: chapter["period"]["title"],
            description: chapter["period"]["description"],
            duration: chapter["period"]["duration"]
        };
    }

    let events = [];
    if (
        chapter["events"] &&
        Array.isArray(chapter["events"]) &&
        chapter["events"].length > 0
    ) {
        events = chapter["events"].map(event => ({
            id: event["uid"],
            title: event["title"],
            start: event["start_date"],
            end: event["end_date"],
            type: event["event_type"]
        }));
    }

    let projects = [];
    if (
        chapter["projects"] &&
        Array.isArray(chapter["projects"]) &&
        chapter["projects"].length > 0
    ) {
        projects = chapter["projects"].map(project => ({
            id: project["uid"],
            title: project["title"],
            start: project["start_date"],
            end: project["end_date"]
        }));
    }

    const chapterObj = {
        id: chapter["uid"],
        title: chapter["title"],
        description: chapter["description"],
        isActive: chapter["is_active"],
        isCurrent: chapter["is_current"],
        period,
        sections,
        events,
        projects
    };
    return chapterObj;
}

export function map2TopicObj(topic) {
    return {
        id: topic["uid"],
        title: topic["title"],
        color: "red",
        discussionCount: topic["discussions_count"],
        description: "description ...",
        active: topic["is_enable"]
    };
}

export function map2DiscussionObj(discussion) {
    return {
        id: discussion["uid"],
        creator: {
            id: discussion["user_info"]["uid"],
            fullName:
                discussion["user_info"]["first_name"] +
                " " +
                discussion["user_info"]["last_name"],
            image: discussion["user_info"]["image"],
            role: discussion["user_info"]["role"]
        },
        postCount: discussion["posts_count"],
        title: discussion["title"],
        content: discussion["content"],
        createAt: new Date(discussion["created"])
    };
}

export function map2PostObj(post) {
    return {
        id: post["uid"],
        user: {
            id: post["user_info"]["uid"],
            fullName:
                post["user_info"]["first_name"] +
                " " +
                post["user_info"]["last_name"],
            image: post["user_info"]["image"],
            role: post["user_info"]["role"]
        },
        content: post["content"],
        sendDate: new Date(post["created"]),
        isLiked: post["user_liked"],
        likes: post["likes"],
        reply: post["reply_to"] && {
            content: post["reply_to"]["content"],
            postId: post["reply_to"]["uid"],
            userName:
                post["reply_to"]["user_info"]["first_name"] +
                " " +
                post["reply_to"]["user_info"]["last_name"],
            userRole: post["reply_to"]["user_info"]["role"]
        },
        attachments: post["post_attachments"].map(a => ({
            id: a["uid"],
            url: a["file"],
            fileType: a["file_type"],
            image: a["image_thumbnail"]
        })),
        mentionedStudents: post["mentioned_students"].map(m => ({
            uid: m["uid"],
            firstName: m["first_name"],
            lastName: m["last_name"],
            role: m["role"],
            image: m["image"]
        }))
    };
}

export function map2ForumUserObj(user) {
    return {
        id: user["uid"],
        firstName: user["first_name"],
        lastName: user["last_name"],
        image: user["image"],
        role: user["role"]
    };
}

export function map2UserObj(user) {
    return {
        id: user["uid"],
        phoneNumber: user["phone"],
        firstName: user["first_name"],
        lastName: user["last_name"],
        email: user["email"],
        profileIsCompleted: user["profile_is_completed"],
        emailVerified: user["email_verified"],
        joinDate: user["date_joined"],
        address: user["address"],
        major: user["major"],
        biography: user["biography"],
        birthday: user["birthday_date"],
        educationDegree: user["education_degree"],
        image: user["image"],
        linkedinUrl: user["linkedin_url"],
        postalCode: user["postal_code"],
        university: user["university"],
        jobTitle: user["job_title"],
        jobPosition: user["occupational_field"]
    };
}

export function map2Quiz(quiz) {
    const questionsObj = quiz["questions"].map(question => {
        const choicesObj = [];
        for (const prop in question["choices"]) {
            if (question["choices"].hasOwnProperty(prop)) {
                const choice = question["choices"][prop];
                choicesObj.push({
                    id: question["uid"] + prop,
                    text: choice["text"]
                });
            }
        }

        return {
            id: question["uid"],
            title: question["title"],
            description: question["description"],
            choices: choicesObj
        };
    });

    return {
        id: quiz["uid"],
        title: quiz["title"],
        summary: quiz["summary"],
        description: quiz["description"],
        isAnswered: quiz["is_answered"],
        status: quiz["status"],
        time: quiz["remaining_time"],
        questions: questionsObj
    };
}

export function map2QuizResult(quizResult) {
    const answers = quizResult["answers"].map(a => {
        return {
            questionId: a["question_uid"],
            correctChoice: a["correct_choice"],
            hint: a["correct_choice_description"]
        };
    });
    const selectedChoice = quizResult["answers"].map(a => {
        return {
            questionId: a["question_uid"],
            choice: a["answer_choice"]
        };
    });
    return {
        grade: quizResult["grade"],
        selectedChoice: selectedChoice,
        correctCount: quizResult["answers_stat"]
            ? quizResult["answers_stat"]["correct"]
            : 0,
        wrongCount: quizResult["answers_stat"]
            ? quizResult["answers_stat"]["wrong"]
            : 0,
        notAnsweredCount: quizResult["answers_stat"]
            ? quizResult["answers_stat"]["not-answered"]
            : quizResult["answers"].length,
        answers
    };
}

export function map2QuizOverview(overview) {
    let total = null;
    let correct = null;
    let wrong = null;
    let status;
    if (!overview["is_active"]) {
        status = "not-active";
    } else {
        if (overview["attempt"] && overview["attempt"]["answers_stat"]) {
            status = "answered";
            total =
                overview["attempt"]["answers_stat"]["wrong"] +
                overview["attempt"]["answers_stat"]["correct"] +
                overview["attempt"]["answers_stat"]["not_answered"];
            correct = overview["attempt"]["answers_stat"]["correct"];
            wrong = overview["attempt"]["answers_stat"]["wrong"];
        } else if (
            overview["attempt"] &&
            !overview["attempt"]["answers_stat"] &&
            overview["attempt"]["is_time_finished"]
        ) {
            status = "time-out";
        } else {
            status = "not-answered";
        }
    }

    return {
        title: overview["title"],
        description: overview["summary"],
        unit: overview["unit_uid"],
        status,
        correctAnswerCount: correct,
        wrongAnswerCount: wrong,
        totalQuestionCount: total
    };
}

export function map2Invoice(invoice) {
    const invoiceItems = invoice["invoice_items"].map((item, index) => {
        return {
            id: item["program"]["uid"],
            title: item["program"]["title"],
            image: item["program"]["image"],
            discount: item["discount"],
            amount: item["amount"],
        };
    });
    return {
        id: invoice["uid"],
        installment: invoice["installment"],
        amount: invoice["total_amount"],
        discount: invoice["total_discount_amount"],
        isPaid: invoice["is_paid"],
        invoiceItems
    };
}

export function map2Gateway(gateway) {
    return {
        id: gateway["uid"],
        title: gateway["title"],
        image: gateway["image"]
    };
}

export function map2EnrollmentResult(result) {
    return {
        isPaid: result["is_paid"],
        isVerified: result["is_verified"],
        programTitle: result["program"]["title"],
        programUID: result["program"]["uid"],
        ProgramStartDate: result["program"]["start_date"],
        studentFullName:
            result["student"]["first_name"] +
            " " +
            result["student"]["last_name"],
        amount: result["program"]["amount"],
        invoiceId: "483197193"
    };
}

export function map2CourseParticipation(participation) {
    return {
        participationRate: participation["participance_rate"],
        teacherScore: participation["lecturer_score"],
        quantityScore: participation["quantity_score"]
    };
}

export function map2CourseProgress(progress) {
    return {
        lockedUnitsCount: progress["locked_units_count"],
        seenUnitsCount: progress["seen_units_count"],
        totalUnitsCount: progress["total_units_count"]
    };
}

export function map2ProgramProgressObj(programProgress) {
    return {
        totalUnitsCount: programProgress["total_units_count"],
        seenUnitsCount: programProgress["seen_units_count"]
    };
}

export function map2EnrollmentObj(enrollment) {
    return {
        id: enrollment["uid"],
        enrollmentDate: enrollment["enroll_date"],
        invoiceId: enrollment["invoice_uid"]
    };
}

export function map2ReportCardObj(reportCard) {
    const factors = Object.entries(reportCard["score_factors"]).map(item => {
        let [key, value] = item;
        if (!value) {
            return null;
        }
        return {
            key,
            coefficient: value["coefficient"],
            totalScore: value["total_score"],
            score: value[`${key}_score`]
        };
    });
    return {
        reportDate: reportCard["create_report_cards_date"],
        totalScore: reportCard["total_score"],
        factors
    };
}

export function map2ForumAttachments(attachment) {
    return {
        id: attachment["uid"],
        fileType: attachment["file_type"],
        url: attachment["file"],
        image: attachment["image_thumbnail"]
    };
}

export function map2Project(project) {
    return {
        id: project["uid"],
        title: project["title"],
        description: project["description"],
        descriptionFile: project["file"],
        start: new Date(project["start_date"]),
        end: new Date(project["end_date"]),
        fileName: null,
        fileStatus:
            project["user_project"] !== null &&
            project["user_project"] !== undefined
                ? "uploaded"
                : "empty",
        uploadedProject:
            project["user_project"] !== null &&
            project["user_project"] !== undefined
                ? project["user_project"]["file"]
                : null,
        uploadProgress: 0,
        fileVerified: false,
        comment: {
            text:
                project["user_project"] !== null &&
                project["user_project"] !== undefined
                    ? project["user_project"]["lecturer_comment"]
                    : null,
            file:
                project["user_project"] !== null &&
                project["user_project"] !== undefined
                    ? project["user_project"]["lecturer_file"]
                    : null
        }
    };
}

export function map2Event(event) {
    return {
        id: event["uid"],
        title: event["title"],
        description: event["description"],
        link: event["link"],
        type: event["event_type"],
        start: event["start_date"],
        end: event["end_date"],
        chapter: event["chapter"]["uid"],
        video: event["archived_video"],
        attachments: event["event_attachments"]
    };
}

export function map2ProjectOverview(projectOverview) {
    return {
        id: projectOverview["uid"],
        title: projectOverview["title"],
        status: projectOverview["status"],
        score: projectOverview["lecturer_score"]
    };
}

export function map2accessObj(access) {
    return {
        accessFrom: access["show_forum"],
        accessExecute: access["show_execute"],
        accessOverview: access["show_overview"]
    };
}

export function map2Period(period) {
    return {
        id: period["uid"],
        title: period["title"],
        description: period["description"] || "",
        duration: period["duration"]
    };
}

/**
 * map data
 * @param  {Object} chat response data object that received from server
 * @return {Chat}
 */
export function map2ChatObj(chat) {
    const chatObj = {
        id: chat["uid"],
        chatType: chat["chat_type"],
        created: chat["created"],
        lastMessage: chat["last_message"],
        unseenMessages: chat["unseen_messages"],
        receiver: {
            id: chat["receiver"]["uid"],
            fullName:
                chat["receiver"]["first_name"] +
                " " +
                chat["receiver"]["last_name"],
            image: chat["receiver"]["image"],
            role: chat["receiver"]["role"]
        }
    };
    return chatObj;
}

export function map2ChatBriefObj(chat) {
    const chatObj = {
        id: chat["uid"],
        created: chat["created"],
        chatType: chat["chat_type"]
    };
    return chatObj;
}

export function map2ChatMessageObj(chatMessage) {
    const chatMessageObj = {
        id: chatMessage["uid"],
        created: chatMessage["created"],
        sender: {
            id: chatMessage["sender"]["uid"],
            fullName:
                chatMessage["sender"]["first_name"] +
                " " +
                chatMessage["sender"]["last_name"],
            image: chatMessage["sender"]["image"],
            role: chatMessage["sender"]["role"]
        },
        chat: {
            uid: chatMessage["chat"]["uid"],
            created: chatMessage["chat"]["created"],
            chatType: chatMessage["chat"]["chat_type"],
            receiver: chatMessage["chat"]["receiver"]
        },
        text: chatMessage["text"],
        file: chatMessage["file"],
        fileSize: chatMessage["file_size"],
        is_seen: chatMessage["is_seen"]
    };
    return chatMessageObj;
}

export function map2LibraryDocumentObj(document) {
    return {
        id: document["uid"],
        title: document["title"],
        description: document["description"],
        image: document["image"],
        thumbnail: document["image_thumbnail"],
        file: document["file"],
        link: document["file_link"],
        createdAt: document["created"],
        authorName: document["author_name"]
    };
}

export function map2LibraryVideoObj(video) {
    return {
        id: video["uid"],
        title: video["title"],
        description: video["description"],
        image: video["image"],
        thumbnail: video["image_thumbnail"],
        file: video["file"],
        link: video["file_link"],
        createdAt: video["created"],
        authorName: video["author_name"]
    };
}
